import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useEmailInbox } from '../contexts/EmailInboxContext';
import { DEMO_FILINGS, DEMO_JOBS, type FilingStatus } from '../data/demoProxyData';

type Filter = 'all' | 'ingested' | 'pending';

const STATUS_STYLE: Record<FilingStatus, { bg: string; fg: string; label: string }> = {
  ingested: { bg: '#F0FDF4', fg: '#166534', label: 'Ingested' },
  pending:  { bg: '#FFF7ED', fg: '#9a3412', label: 'Waiting' },
  running:  { bg: '#EFF6FF', fg: '#1d4ed8', label: 'Running' },
};

function wait(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms));
}

const SOURCE_STYLE: Record<string, string> = {
  'SEC EDGAR': '#1d4ed8',
  'SEDAR':     '#7c3aed',
};

export function ProxyFilingsPage() {
  const { setTopbar } = useAppShell();
  const { appendOutbound } = useEmailInbox();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>('all');
  const [filings, setFilings] = useState(DEMO_FILINGS);
  const [running, setRunning] = useState(false);
  const [ran, setRan] = useState(false);
  const [queriedCount, setQueriedCount] = useState(0);

  useEffect(() => {
    setTopbar('EDGAR / SEDAR', 'Acquisition queue — job filings pulled from SEC EDGAR and SEDAR');
  }, [setTopbar]);

  // React to demo step: step 2 → ingested, step 3/4 → pending, others → all
  useEffect(() => {
    const idea = parseInt(searchParams.get('idea') ?? '0', 10);
    const step = parseInt(searchParams.get('step') ?? '0', 10);
    if (idea !== 2 || !step) return;
    if (step === 2) setFilter('ingested');
    else if (step === 3 || step === 4) setFilter('pending');
    else setFilter('all');
  }, [searchParams]);

  const rows = filings.filter(f => filter === 'all' || (filter === 'pending' ? f.status !== 'ingested' : f.status === filter));
  const pendingCount = filings.filter(f => f.status !== 'ingested').length;

  async function runAcquisition() {
    if (running) return;
    const pending = filings.filter(f => f.status === 'pending');
    if (!pending.length) return;
    setRunning(true);
    for (const f of pending) {
      setFilings(prev => prev.map(row =>
        row.jobId === f.jobId ? { ...row, status: 'running', note: `Querying ${f.source}…` } : row,
      ));
      await wait(700);
      setFilings(prev => prev.map(row =>
        row.jobId === f.jobId
          ? { ...row, status: 'ingested', note: `${f.docType} · ${f.pages} pages · in verifications@` }
          : row,
      ));
      const job = DEMO_JOBS[f.jobId];
      const issuer = job?.issuer ?? f.jobId;
      appendOutbound({
        refKey: `acquisition-${f.jobId}`,
        jobIds: [f.jobId],
        origin: 'proxy',
        to: 'verifications@betanxt.com',
        subject: `${f.docType} — ${issuer} (Job ${f.jobId})`,
        body: `Retrieved from ${f.source} by CUSIP ${job?.cusip ?? '—'}. ${f.pages} pages. Document type: ${f.docType}. Placed in verifications@ for the verification desk.`,
      });
    }
    setRunning(false);
    setRan(true);
    setQueriedCount(pending.length);
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex gap-1.5">
            {(['all', 'ingested', 'pending'] as Filter[]).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border"
                style={{
                  background:  filter === f ? '#043243' : 'white',
                  color:       filter === f ? 'white'   : '#595A60',
                  borderColor: filter === f ? '#043243' : '#E5E7EB',
                }}
              >
                {f === 'all' ? 'All filings' : f === 'ingested' ? 'Ingested' : 'Waiting'}
                <span className="ml-1.5 opacity-60">
                  {f === 'all' ? filings.length : f === 'ingested' ? filings.filter(x => x.status === 'ingested').length : pendingCount}
                </span>
              </button>
            ))}
          </div>
          <button
            data-tour="filings-run"
            onClick={runAcquisition}
            disabled={running || pendingCount === 0}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-white transition-opacity disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#14b8a6,#0891b2)' }}
          >
            {running ? 'Acquiring…' : pendingCount > 0 ? 'Run acquisition' : 'Acquisition complete'}
          </button>
        </div>

        {ran && !running && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 flex items-center justify-between">
            <span>Acquisition complete — {queriedCount} job{queriedCount === 1 ? '' : 's'} queried. PDFs are in verifications@.</span>
            <button onClick={() => navigate('/email-inbox')} className="font-semibold underline hover:no-underline shrink-0 ml-3">
              View inbox →
            </button>
          </div>
        )}

        {/* Table */}
        <div data-tour="filings-table" className="rounded-2xl border border-trim overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-trim bg-[#F8FAFC]">
                <th className="text-left px-4 py-3 font-semibold text-muted">Job</th>
                <th className="text-left px-4 py-3 font-semibold text-muted">Issuer</th>
                <th className="text-left px-4 py-3 font-semibold text-muted">Market</th>
                <th className="text-left px-4 py-3 font-semibold text-muted">Source</th>
                <th className="text-left px-4 py-3 font-semibold text-muted">Document</th>
                <th className="text-right px-4 py-3 font-semibold text-muted">Pages</th>
                <th className="text-right px-4 py-3 font-semibold text-muted">Days left</th>
                <th className="text-left px-4 py-3 font-semibold text-muted">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const job = DEMO_JOBS[f.jobId];
                const st  = STATUS_STYLE[f.status];
                const isIngested = f.status === 'ingested';
                return (
                  <tr
                    key={f.jobId}
                    data-tour={isIngested ? 'filing-ingested' : 'filing-pending'}
                    className="border-b border-trim last:border-0 hover:bg-[#F8FAFC] transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-muted">{f.jobId}</td>
                    <td className="px-4 py-3 font-medium text-ink">{job?.issuer ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border"
                        style={{ background: job?.market === 'CA' ? '#EEF2FF' : '#EFF6FF', color: SOURCE_STYLE[f.source], borderColor: 'transparent' }}>
                        {job?.market ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted">{f.source}</td>
                    <td className="px-4 py-3 text-ink">{f.docType} · {f.pages} pp</td>
                    <td className="px-4 py-3 text-right font-mono text-muted">{f.pages}</td>
                    <td className="px-4 py-3 text-right font-mono" style={{ color: f.daysLeft <= 5 ? '#b91c1c' : f.daysLeft <= 10 ? '#92400e' : '#166534' }}>
                      {f.daysLeft}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold" style={{ background: st.bg, color: st.fg }}>
                        {st.label}
                      </span>
                      <span className="ml-2 text-muted">{f.note}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer note */}
        <p className="mt-4 text-xs text-muted">
          US jobs query SEC EDGAR by CUSIP · CA jobs query SEDAR · PDFs placed in verifications@ on acquisition
        </p>
      </div>
    </div>
  );
}
